import {useCallback, useMemo} from 'react';

//Utils
import {generatePaxString} from '@stencil/hotels-utils/src/convert-to-room-string';
import {isObjectEmpty} from '@stencil/hotels-utils/src/common-utils';
import {MultiRoomConfig} from '@stencil/hotels-types/src/common/types';

interface AppliedFiltersDispatchAction {
  type: 'APPLY' | 'REMOVE';
  payload: {
    filterGroup: string;
    filterObj: {
      value?: string;
      title?: string;
    };
    isMultiple?: boolean;
  };
}

interface UseMultiRoomConfigProps {
  multiRoomConfig: MultiRoomConfig;
  roomCount: number;
  adultCount: number;
  childCount: number;
  appliedFiltersObj: Record<string, unknown>;
  appliedFiltersDispatch: React.Dispatch<AppliedFiltersDispatchAction>;
  isDonotShowMultiRoomFilter?: boolean;
}

const useMultiRoomConfig = ({
  multiRoomConfig,
  roomCount,
  adultCount,
  childCount,
  isDonotShowMultiRoomFilter,
  appliedFiltersObj,
  appliedFiltersDispatch,
}: UseMultiRoomConfigProps) => {
  const multiRoomFilterConfig = useMemo(() => {
    const isShowMultiRoomFilter =
      !!multiRoomConfig?.offerText &&
      multiRoomConfig?.minRoomCount <= roomCount &&
      multiRoomConfig?.minAdultCount <= adultCount &&
      multiRoomConfig?.minChildCount <= childCount &&
      !isDonotShowMultiRoomFilter;

    let multiRoomPaxString = '';

    if (isShowMultiRoomFilter) {
      const requiredPaxString = generatePaxString(adultCount, childCount);
      multiRoomPaxString = multiRoomConfig?.offerText?.replace('{pax_string}', requiredPaxString);
    }

    return {
      isShowMultiRoomFilter,
      multiRoomPaxString,
    };
  }, [multiRoomConfig, roomCount, adultCount, childCount]);

  const isMultiRoomFilterSelect = useMemo(() => {
    const filterKey = multiRoomConfig?.filterInfo?.filterGroup;
    if (isObjectEmpty(appliedFiltersObj) || !filterKey) {
      return false;
    }
    return appliedFiltersObj?.hasOwnProperty(filterKey);
  }, [appliedFiltersObj, multiRoomConfig]);

  const multiRoomFilterApplyCaller = useCallback(() => {
    if (!isObjectEmpty(multiRoomConfig?.filterInfo)) {
      const {filterValue, filterGroup, filterTitle} = multiRoomConfig?.filterInfo || {};
      const isMultiple = false;
      const filterObj = {
        value: filterValue,
        title: filterTitle,
      };
      if (appliedFiltersObj?.hasOwnProperty(filterGroup)) {
        appliedFiltersDispatch({
          type: 'REMOVE',
          payload: {filterGroup, filterObj, isMultiple},
        });
      } else {
        appliedFiltersDispatch({
          type: 'APPLY',
          payload: {filterGroup, filterObj, isMultiple},
        });
      }
    }
  }, [
    multiRoomConfig?.filterInfo?.filterValue,
    multiRoomConfig?.filterInfo?.filterGroup,
    multiRoomConfig?.filterInfo?.filterTitle,
    appliedFiltersObj,
  ]);

  return {multiRoomFilterApplyCaller, isMultiRoomFilterSelect, multiRoomFilterConfig};
};

export default useMultiRoomConfig;
